import AsyncSelect from 'react-select/async';
import axios from 'axios';
import { useFormContext, Controller } from 'react-hook-form';
import { debounce } from '../pages/Admin/Attachments/RecordsTable';

interface RecordSelectorProps {
  name: string;
  isMulti?: boolean;
  searchUrl: string;
  label?: string;
  formatOptionLabel?: (option: any, meta: any, onSelect: (option: any) => void) => React.ReactNode;
  footer?: (field: { value: any; onChange: (v: any) => void }) => React.ReactNode;
}

export default function RecordSelector({
  name,
  isMulti = false,
  searchUrl,
  label,
  formatOptionLabel,
  footer,
}: RecordSelectorProps) {
  const { control } = useFormContext();

  const loadOptions = debounce(async (inputValue: string, callback: (options: any[]) => void) => {
    const response = await axios.get(searchUrl, {
      params: {
        query: inputValue,
      }
    })
    const options = response.data
    callback(options);
  }, 400);

  const defaultFormatOptionLabel = (option: any) => {
    return (
      <div>
        <div className="font-medium">{option.label}</div>
        {option.description && (
          <div className="text-sm text-gray-600">{option.description}</div>
        )}
      </div>
    )
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <fieldset
          className="fieldset"
          style={{
            padding: 0,
            display: 'inline-block',
          }}
        >
          <legend
            className="fieldset-legend"
            style={{
              paddingTop: 0,
              paddingBottom: 4,
            }}
          >
            {label || (isMulti ? 'Records' : 'Record')}
          </legend>
          <AsyncSelect
            cacheOptions
            isMulti={isMulti}
            loadOptions={loadOptions as any}
            value={field.value}
            onChange={field.onChange}
            formatOptionLabel={
              formatOptionLabel
                ? (option: any, meta: any) => {
                    const onSelect = (selected: any) =>
                      field.onChange(isMulti ? [...(field.value || []), selected] : selected);
                    return formatOptionLabel(option, meta, onSelect);
                  }
                : defaultFormatOptionLabel
            }
            loadingMessage={() => 'Searching...'}
            noOptionsMessage={({ inputValue }) =>
              inputValue ? `No records found for "${inputValue}"` : 'Type to search'
            }
            defaultOptions={true}
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={{
              container: (styles) => ({
                ...styles,
                width: 'fit-content',
                minWidth: '325px',
                maxWidth: '400px',
              }),
              control: (styles) => ({
                ...styles,
                borderRadius: 0,
                width: 'auto',
                backgroundColor: 'var(--color-base-200)',
                cursor: 'pointer',
              }),
              valueContainer: (styles) => ({
                ...styles,
                width: 'auto',
                maxHeight: '150px',
                overflowY: 'auto',
              }),
              input: (styles) => ({
                ...styles,
                cursor: 'text',
              }),
              option: (styles) => ({
                ...styles,
                cursor: 'pointer',
              }),
              menuPortal: (styles) => ({
                ...styles,
                zIndex: 9999,
              }),
            }}
          />
          {footer?.(field)}
        </fieldset>
      )}
    />
  )
}
